import {Component, EventEmitter, inject, Input, OnInit, Output, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {EventService} from '../../../../shared/services/event.service';
import {AlertService} from '../../../../shared/services/alert.service';
import {Messages} from '../../../../shared/constants/messages';
import {Event} from '../../../../shared/model/event';
import {EventFormComponent} from '../event-form/event-form.component';
import {Organization} from '../../../../shared/model/organization';
import {PermissionService} from '../../../../core/services/permission.service';

@Component({
  selector: 'app-event-edit',
  standalone: true,
  imports: [EventFormComponent],
  templateUrl: './event-edit.component.html',
  styleUrl: './event-edit.component.css'
})
export class EventEditComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private eventService: EventService = inject(EventService);
  private alertService: AlertService = inject(AlertService);
  private permissionService: PermissionService = inject(PermissionService);

  /** Se valorizzato il componente lavora in modalità embedded (es. modale admin): niente route né navigazione. */
  @Input() embeddedId?: number;
  @Output() saved = new EventEmitter<Event>();
  @Output() cancelled = new EventEmitter<void>();

  protected event = signal<Event | undefined>(undefined);
  protected availableOrganizations = signal<Organization[]>([]);

  ngOnInit(): void {
    if (this.embeddedId) {
      this.loadEvent(this.embeddedId);
      return;
    }
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.loadEvent(Number(idParam));
    } else {
      this.alertService.showError(Messages.EVENT_NOT_FOUND);
      this.router.navigate(['/events']);
    }
  }

  onSubmit(eventData: Partial<Event>) {
    const currentEvent = this.event();
    if (!currentEvent?.id) {
      return;
    }
    const payload: Event = {
      ...currentEvent,
      ...eventData,
      id: currentEvent.id,
      startDate: eventData.startDate ?? currentEvent.startDate,
      endDate: eventData.endDate,
    } as Event;
    this.eventService.updateEvent(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.alertService.showSuccess(Messages.EVENT_UPDATED_SUCCESS);
          if (this.embeddedId) {
            this.saved.emit(response.data ?? payload);
            return;
          }
          this.router.navigate(['/events/detail', currentEvent.id]);
        }
      },
      error: (err) => {
        if (!err?.error?.message) {
          this.alertService.showError(Messages.EVENT_SAVE_ERROR);
        }
      }
    });
  }

  onCancel() {
    if (this.embeddedId) {
      this.cancelled.emit();
      return;
    }
    const currentEvent = this.event();
    if (currentEvent?.id) {
      this.router.navigate(['/events/detail', currentEvent.id]);
    } else {
      this.router.navigate(['/events']);
    }
  }

  private loadEvent(id: number) {
    this.eventService.getEventById(id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const transformed: Event = {
            ...response.data,
            startDate: response.data.startDate,
            endDate: response.data.endDate,
          };
          if (!this.permissionService.canViewDisabledEvent(transformed)) {
            this.handleMissing();
            return;
          }
          this.event.set(transformed);
          this.availableOrganizations.set(this.toOrganizationOptions(transformed));
        } else {
          this.handleMissing();
        }
      },
      error: () => {
        this.handleMissing();
      }
    });
  }


  private toOrganizationOptions(event: Event): Organization[] {
    if (!event.organization) {
      return [];
    }
    return [{
      id: event.organization.id,
      organizationName: event.organization.organizationName,
      organizationLogo: event.organization.organizationLogo,
      organizationActive: true,
      member: true,
      moderator: false,
      admin: false,
      owner: false
    }];
  }

  private handleMissing() {
    this.alertService.showError(Messages.EVENT_LOAD_ERROR);
    if (this.embeddedId) {
      this.cancelled.emit();
      return;
    }
    this.router.navigate(['/events']);
  }
}
